import { forwardRef, useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";

/**
 * MagneticButton — subtle cursor-magnet effect.
 * `as` can be a string tag ("button", "a") or a React component (e.g. Link).
 */

// Framer Motion caches its own factories, but component-based wrappers need to be
// stable across renders to avoid unmount cycles. This cache keeps a single motion
// wrapper per component reference for the lifetime of the module.
const componentCache = new WeakMap();
function motionOf(as) {
  if (typeof as === "string") return motion[as] || motion.button;
  const cached = componentCache.get(as);
  if (cached) return cached;
  const wrapper =
    typeof motion.create === "function" ? motion.create(as) : motion(as);
  componentCache.set(as, wrapper);
  return wrapper;
}

const MagneticButton = forwardRef(function MagneticButton(
  { as = "button", variant = "primary", className = "", children, ...rest },
  ref
) {
  const localRef = useRef(null);
  const setRef = (node) => {
    localRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };
  const reduced = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 18, mass: 0.4 });

  const onMove = (e) => {
    if (reduced) return;
    const el = localRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    const s = 0.28;
    const max = 14;
    x.set(Math.max(-max, Math.min(max, relX * s)));
    y.set(Math.max(-max, Math.min(max, relY * s)));
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Comp = useMemo(() => motionOf(as), [as]);

  const base =
    "relative inline-flex items-center justify-center gap-2 rounded-full font-body font-medium px-5 py-2.5 md:px-6 md:py-3 select-none";
  const styles =
    variant === "primary"
      ? "text-ink bg-gradient-to-b from-[#FFD37A] via-[#F5B841] to-[#C89026] shadow-[0_10px_30px_-10px_rgba(245,184,65,0.55)] hover:shadow-[0_14px_40px_-8px_rgba(245,184,65,0.8)] transition-shadow duration-300 border border-gold/60"
      : "text-inkText-primary border border-gold/25 hover:border-gold hover:text-gold-body bg-ink-panel/30 backdrop-blur-sm transition-colors duration-300";

  return (
    <Comp
      ref={setRef}
      style={{ x: sx, y: sy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={[base, styles, className].join(" ")}
      {...rest}
    >
      {variant === "primary" && (
        <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-black/10" />
      )}
      <span className="relative z-10 flex items-center gap-2 tracking-wide">
        {children}
      </span>
    </Comp>
  );
});

export default MagneticButton;
